import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Divider } from '@mui/material';
import apis from '@src/apis';
import i18n from '@src/languages';
import Card from '@src/components/Card';
import Table from '@src/new-components/Table';
import Typography from '@src/new-components/Typography';
import { delimitNumber } from '@src/utils/number';
import { PAGINATION_LIMIT } from '@src/constants';
import { WITHDRAWAL_REQUEST_SORT } from '@src/constants/withdrawal';
import { COLOR } from '@src/styles/color';
import EmptyOrders from '@src/containers/EarningsManagement/TransactionsHistory/EmptyWithdrawalRequest';
import { getPackageName } from '@src/services/package';
import Tooltip from '@src/components/Tooltip';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { StyledReferredOrders } from './index.style';
import ReferredOrdersHeader from './Header';

const ReferredOrders = () => {
  const { t } = useTranslation();
  const { language } = i18n;
  const { headerVoucher } = useSelector((state) => state.voucher);

  const [orders, setOrders] = useState([]);
  const [paging, setPaging] = useState({ page: 0, total: 0 });
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_LIMIT);
  const [loading, setLoading] = useState(true);

  const isShowHeaderBanner = headerVoucher && headerVoucher.length > 0;

  const offsetRequest = useMemo(
    () => paging?.page * rowsPerPage,
    [rowsPerPage, paging?.page],
  );

  const fetchReferredOrders = async () => {
    setLoading(true);
    const data = await apis.orders.getReferredOrders({
      offset: offsetRequest,
      limit: rowsPerPage,
      sort: WITHDRAWAL_REQUEST_SORT.CREATED_AT_DESC,
    });
    if (data && data.status) {
      setOrders(data.result?.orders);
      setPaging({ ...paging, total: data.result?.total });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReferredOrders();
  }, [rowsPerPage, offsetRequest]);

  const renderTypography = (content) => (
    <Typography variant="body-14-regular" color={COLOR.darkGray}>
      {content === 'null' || content === 'undefined' ? '' : content}
    </Typography>
  );

  const renderMoneyTypography = (content) => (
    <Typography variant="body-14-regular" color={COLOR.darkGray}>
      <span className="vnd">đ</span>
      {content}
    </Typography>
  );

  const handleChangePage = (page) => {
    setPaging({ ...paging, page });
  };

  const formatDate = (date) => {
    if (date) return moment(date).format('DD/MM/YYYY - HH:mm');
    return '-';
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value);
    setPaging({ ...paging, page: 0 });
  };

  const renderTitleReferredOrdersPrice = () => (
    <Box style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
      <Typography color={COLOR.darkGray} variant="body-14-medium">
        {t('referredOrdersPrice')}
      </Typography>
      <Tooltip
        arrow
        placement="top"
        title={t('referredOrdersPriceTooltip')}
        maxWidth="190px"
      >
        <InfoRoundedIcon sx={{ color: COLOR.indigoIcon, fontSize: '16px' }} />
      </Tooltip>
    </Box>
  );

  const getColumnsReferredOrders = () => [
    {
      title: t('referredOrdersDate'),
      align: 'center',
      field: 'confirmedAt',
      render: (row) => renderTypography(formatDate(row.confirmedAt)),
    },
    {
      title: t('accountID'),
      align: 'center',
      field: 'accountId',
      render: (row) => renderTypography(row.userId),
    },
    {
      title: t('package'),
      align: 'center',
      field: 'package',
      render: (row) =>
        renderTypography(
          getPackageName(
            row?.package?.name,
            row?.package?.code,
            language,
            t,
            true,
            true,
          ),
        ),
    },
    {
      title: renderTitleReferredOrdersPrice(),
      align: 'center',
      field: 'paidAt',
      render: (row) =>
        renderMoneyTypography(delimitNumber(row.price, language)),
    },
  ];

  const columns = getColumnsReferredOrders();

  return (
    <StyledReferredOrders isShowHeaderBanner={isShowHeaderBanner}>
      <Card flexDirection="column" className="transaction-history-card">
        <Box className="transaction-history-wrapper">
          <ReferredOrdersHeader />
          <Divider sx={{ margin: '24px' }} />
          <div className="transaction-history-table">
            {loading ? (
              <div className="transaction-history-loading">
                <CircularProgress />
              </div>
            ) : (
              <>
                {orders?.length ? (
                  <Table
                    columns={columns}
                    headerStyle={{ borderRadius: '16px 16px 0 0' }}
                    data={orders}
                    page={paging.page}
                    total={paging.total}
                    rowsPerPage={rowsPerPage}
                    onChangeRowPage={handleChangeRowsPerPage}
                    onChangePage={handleChangePage}
                    rowsPerPageOptions={[5, 10, 25]}
                    styleFooter={{
                      padding: '16px 16px 16px 0px',
                      height: 'auto',
                    }}
                    isRowClickable={false}
                    minWidth="1024px"
                  />
                ) : (
                  <EmptyOrders description="noData" isDescription />
                )}
              </>
            )}
          </div>
        </Box>
      </Card>
    </StyledReferredOrders>
  );
};

export default ReferredOrders;
